import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Pencil, Trash2, DollarSign, ArrowRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { CargaMasivaButton } from '../components/CargaMasivaButton';

export const TarifasPage = () => {
  const [tarifas, setTarifas] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentTarifa, setCurrentTarifa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    origen: '',
    destino: '',
    tarifa: ''
  });

  // Filtrar tarifas basado en el término de búsqueda
  const filteredTarifas = useMemo(() => {
    if (!searchTerm.trim()) return tarifas;
    const term = searchTerm.toLowerCase();
    return tarifas.filter(tarifa => 
      tarifa.origen?.toLowerCase().includes(term) ||
      tarifa.destino?.toLowerCase().includes(term) ||
      tarifa.tarifa?.toString().includes(term) ||
      tarifa.tarifa?.toLocaleString('es-CL').includes(term)
    );
  }, [tarifas, searchTerm]);

  useEffect(() => {
    loadTarifas();
  }, []);

  const loadTarifas = async () => {
    try {
      const response = await api.get('/tarifas');
      setTarifas(response.data);
    } catch (error) {
      toast.error('Error al cargar tarifas');
    }
  };

  const handleOpenDialog = (tarifa = null) => {
    if (tarifa) {
      setCurrentTarifa(tarifa);
      setFormData({
        origen: tarifa.origen,
        destino: tarifa.destino,
        tarifa: tarifa.tarifa.toString()
      });
    } else {
      setCurrentTarifa(null);
      setFormData({ origen: '', destino: '', tarifa: '' });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        tarifa: parseFloat(formData.tarifa)
      };
      
      if (currentTarifa) {
        await api.put(`/tarifas/${currentTarifa.id}`, data);
        toast.success('Tarifa actualizada');
      } else {
        await api.post('/tarifas', data);
        toast.success('Tarifa creada');
      }
      await loadTarifas();
      setShowDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar tarifa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/tarifas/${currentTarifa.id}`);
      toast.success('Tarifa eliminada');
      await loadTarifas();
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Error al eliminar tarifa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="tarifas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Tarifas</h1>
          <p className="text-gray-600 mt-1">Gestión de tarifas por ruta</p>
        </div>
        <div className="flex gap-2">
          <CargaMasivaButton
            endpoint="/tarifas/carga-masiva"
            onSuccess={loadTarifas}
            templateColumns={['origen', 'destino', 'tarifa']}
            templateName="tarifas"
            entityName="Tarifas"
          />
          <Button onClick={() => handleOpenDialog()} data-testid="create-tarifa-button">
            <Plus size={18} className="mr-2" />
            Nueva Tarifa
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          {/* Buscador */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar por origen, destino o tarifa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-tarifas-input"
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {filteredTarifas.length} resultado{filteredTarifas.length !== 1 ? 's' : ''} encontrado{filteredTarifas.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Origen</TableHead>
                <TableHead></TableHead>
                <TableHead>Destino</TableHead>
                <TableHead>Tarifa</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTarifas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    {searchTerm ? 'No se encontraron tarifas con ese criterio' : 'No hay tarifas registradas'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredTarifas.map((tarifa) => (
                  <TableRow key={tarifa.id}>
                    <TableCell className="font-medium">{tarifa.origen}</TableCell>
                    <TableCell className="text-center">
                      <ArrowRight size={16} className="text-gray-400 mx-auto" />
                    </TableCell>
                    <TableCell className="font-medium">{tarifa.destino}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="font-bold text-primary">
                          ${tarifa.tarifa.toLocaleString('es-CL')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(tarifa)}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCurrentTarifa(tarifa);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="tarifa-dialog">
          <DialogHeader>
            <DialogTitle>
              {currentTarifa ? 'Editar Tarifa' : 'Nueva Tarifa'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="origen">Origen *</Label>
              <Input
                id="origen"
                value={formData.origen}
                onChange={(e) => setFormData({ ...formData, origen: e.target.value })}
                required
                placeholder="Ej: Santiago Centro"
                data-testid="origen-input"
              />
            </div>

            <div>
              <Label htmlFor="destino">Destino *</Label>
              <Input
                id="destino"
                value={formData.destino}
                onChange={(e) => setFormData({ ...formData, destino: e.target.value })}
                required
                placeholder="Ej: Aeropuerto"
                data-testid="destino-input"
              />
            </div>

            <div>
              <Label htmlFor="tarifa">Tarifa (CLP) *</Label>
              <Input
                id="tarifa"
                type="number"
                value={formData.tarifa}
                onChange={(e) => setFormData({ ...formData, tarifa: e.target.value })}
                onWheel={(e) => e.target.blur()}
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                required
                placeholder="25000"
                data-testid="tarifa-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-tarifa-button">
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar la tarifa <strong>{currentTarifa?.origen} → {currentTarifa?.destino}</strong>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};